import type { PatientSearchItem } from "@elation/api-client/src/stitch-web/models";

import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import {
  ComboboxEmpty,
  ComboboxList,
} from "@src/shared/components/v2/Combobox";
import { cn } from "@src/shared/lib/utils";

import type { PatientSearchOption } from "../PatientSearch.types";
import { PatientSearchOptionRow } from "./PatientSearchOptionRow";

interface PatientSearchResultsProps {
  emptyMessage: string;
  error: unknown;
  isError: boolean;
  isOpening: boolean;
  isSearching: boolean;
  // True while a newer query is in flight but the (non-empty) stale options
  // are still shown as `placeholderData` — see usePatientSearchOptions.
  // Distinct from `isSearching` (which only covers the empty-options case)
  // so stale-but-selectable options get a busy affordance instead of
  // silently looking like a settled match for the current query.
  isRefetching: boolean;
  listboxLabel: string;
  onSelect: (patient: PatientSearchItem) => void;
  openingKey: string | null;
  options: PatientSearchOption[];
  selectingLabel: string;
}

export function PatientSearchResults({
  emptyMessage,
  error,
  isError,
  isOpening,
  isSearching,
  isRefetching,
  listboxLabel,
  onSelect,
  openingKey,
  options,
  selectingLabel,
}: PatientSearchResultsProps) {
  if (isError) {
    return (
      <ErrorMessage
        className="block p-3"
        error={error}
        fallback="Unable to search patients."
      />
    );
  }

  if (isSearching) {
    return (
      <VerticalFlexbox className="gap-2 p-3">
        {[0, 1, 2].map((index) => (
          <HorizontalFlexbox key={index} className="gap-2.5">
            <Skeleton className="size-8 rounded-full" />
            <VerticalFlexbox className="flex-1 gap-1">
              <Skeleton className="h-3 w-28" />
              <Skeleton className="h-2.5 w-40" />
            </VerticalFlexbox>
          </HorizontalFlexbox>
        ))}
      </VerticalFlexbox>
    );
  }

  if (options.length === 0) {
    return (
      <ComboboxEmpty className="px-4 py-6 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </ComboboxEmpty>
    );
  }

  return (
    <ComboboxList<PatientSearchOption>
      aria-label={listboxLabel}
      aria-busy={isRefetching}
      className={cn(isRefetching && "opacity-60 transition-opacity")}
      items={options}
    >
      {(option) => (
        <PatientSearchOptionRow
          key={option.key}
          option={option}
          isDisabled={isOpening}
          isSelecting={isOpening && openingKey === option.key}
          selectingLabel={selectingLabel}
          onSelect={(patient) => void onSelect(patient)}
        />
      )}
    </ComboboxList>
  );
}
